import React, { useState } from 'react';
import { NavLink, useNavigate, Outlet, useLocation } from 'react-router-dom';
import {
  FiGrid, FiPackage, FiTag, FiShoppingBag, FiImage, FiUsers, FiMail,
  FiMenu, FiX, FiLogOut, FiHome, FiSettings, FiChevronRight
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { getImageUrl } from '../../utils/helpers';

const navItems = [
  { to: '/admin', label: 'Dashboard', icon: FiGrid, end: true },
  { to: '/admin/products', label: 'Products', icon: FiPackage },
  { to: '/admin/categories', label: 'Categories', icon: FiTag },
  { to: '/admin/orders', label: 'Orders', icon: FiShoppingBag },
  { to: '/admin/banners', label: 'Banners & Images', icon: FiImage },
  { to: '/admin/users', label: 'Users', icon: FiUsers },
  { to: '/admin/messages', label: 'Messages', icon: FiMail },
];

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => { logout(); navigate('/'); };

  // Build breadcrumb from path
  const pathParts = location.pathname.replace('/admin', '').split('/').filter(Boolean);
  const breadcrumb = ['Admin', ...pathParts.map((p) => p.charAt(0).toUpperCase() + p.slice(1))];

  const navClass = ({ isActive }) =>
    `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
      isActive
        ? 'bg-white text-primary-700 shadow-md font-semibold'
        : 'text-primary-100 hover:bg-primary-600/60 hover:text-white'
    }`;

  return (
    <div className="flex h-screen bg-gray-50/80 overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`${sidebarOpen ? 'w-60' : 'w-0 md:w-[72px]'} flex-shrink-0 flex flex-col transition-all duration-300 overflow-hidden`}
        style={{ background: 'linear-gradient(180deg, #1e3a5f 0%, #162d4a 100%)' }}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10">
          {settings.logo ? (
            <img src={getImageUrl(settings.logo)} alt="logo" className="h-9 w-auto object-contain brightness-0 invert flex-shrink-0" />
          ) : (
            <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
              <span className="text-primary-700 font-extrabold text-lg">H</span>
            </div>
          )}
          {sidebarOpen && (
            <div className="overflow-hidden">
              <p className="text-white font-bold text-sm leading-tight truncate">{settings.siteName || 'Hospitex'}</p>
              <p className="text-primary-300 text-xs">Admin Panel</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={navClass}>
              <Icon size={17} className="flex-shrink-0" />
              {sidebarOpen && <span className="truncate">{label}</span>}
            </NavLink>
          ))}

          <div className="border-t border-white/10 my-3" />

          <NavLink to="/admin/settings" className={navClass}>
            <FiSettings size={17} className="flex-shrink-0" />
            {sidebarOpen && <span>Site Settings</span>}
          </NavLink>
        </nav>

        {/* Footer nav */}
        <div className="px-3 py-4 border-t border-white/10 space-y-1">
          <NavLink to="/" className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-primary-200 hover:bg-primary-600/60 hover:text-white transition-colors">
            <FiHome size={16} className="flex-shrink-0" />
            {sidebarOpen && <span className="truncate">View Store</span>}
          </NavLink>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-colors">
            <FiLogOut size={16} className="flex-shrink-0" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Topbar */}
        <header className="bg-white border-b border-gray-200 flex items-center justify-between px-5 py-3.5 shadow-sm">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors"
            >
              {sidebarOpen ? <FiX size={19} /> : <FiMenu size={19} />}
            </button>
            {/* Breadcrumb */}
            <nav className="hidden md:flex items-center gap-1.5 text-sm text-gray-500">
              {breadcrumb.map((part, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <FiChevronRight size={12} className="text-gray-300" />}
                  <span className={i === breadcrumb.length - 1 ? 'text-gray-800 font-semibold' : 'hover:text-primary-600 cursor-pointer'}>
                    {part}
                  </span>
                </React.Fragment>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right hidden md:block">
              <p className="text-sm font-semibold text-gray-800 leading-tight">{user?.name}</p>
              <p className="text-xs text-primary-500 font-medium">Administrator</p>
            </div>
            <div className="w-9 h-9 bg-gradient-to-br from-primary-400 to-primary-700 rounded-xl flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-sm">{user?.name?.[0]}</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
